import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CustomLink from '@gc-npm/components.custom-link';
import TextInfo, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
} from '@gc-npm/components.text';
import Glyph from '@gc-npm/components.icon-glyph';
import { ICON_ID } from '@gc-npm/utils.enums.icon-id';
import sendGaEvent from '@gc-npm/utils.metrics.send-ga-event';
import { actions as modalActions } from '@gc-npm/reducers.modals-reducer';
import { downloadDocuments, getModuleNamesTagginInfo } from '@/utils/index';

import Form from '@/components/FormSkeleton';
import {
  actions as uiActions,
} from '@/reducers/ui';

import LegalsModalForm from './LegalsModalForm'

const COMPONENT_NAME = 'LegalsModal';

const LegalsModal = ({ data }) => {
  const dispatch = useDispatch();

  const [showLegals, setShowLegals] = useState(false);

  const layout = useSelector((state) => state.UI.layout);
  const clientModules = useSelector((state) => state.client.modulos);
  const modulos = useSelector((state) => state.cart.modulos);

  const componentProduct = getModuleNamesTagginInfo(modulos, layout, clientModules);

  const { subject, legals, provisionalAgreements } = data || {};

  const {
    title,
    description,
    descriptionFlyout,
    dropdown,
    legalText,
    legalErrorText,
    legalLinkText,
    urlLegal,
    acceptText,
    cancelText,
  } = legals || {};

  const taggingInfo = {
    componentContent: subject || '',
    componentName: COMPONENT_NAME,
    componentProduct,
    componentVariation: '',
  }

  useEffect(() => {
    sendGaEvent({
      ...taggingInfo,
      eventType: 'print-success',
    })
  }, []);

  const onSubmit = (isAccept) => {
    if (isAccept) {
      dispatch(uiActions.sendBackoffice(data));
    } else {
      dispatch(
        modalActions.addModal({
          id: '',
        }),
      );
    }
  }

  return (
    <>
      <div className="LegalsModal">
        <div className="LegalsModal_header">
          {title && (
            <TextInfo
              kind={TEXT_KIND.PARAGRAPH}
              color={TEXT_COLOR.GREY6}
              size={TEXT_SIZE.T6}
              text={title}
            />
          )}
          <CustomLink
            taggingInfo={{ ...taggingInfo, componentContent: 'Cerrar' }}
            onClick={onSubmit}
          >
            <Glyph
              id={ICON_ID.Cerrar}
              height={14}
              width={14}
              color={TEXT_COLOR.MOVISTAR_BLUE}
            />
          </CustomLink>
        </div>
        {description && (
          <TextInfo
            kind={TEXT_KIND.PARAGRAPH}
            color={TEXT_COLOR.GREY6}
            size={TEXT_SIZE.CAPTION1}
            text={description}
          />
        )}
        {descriptionFlyout && (
          <TextInfo
            kind={TEXT_KIND.PARAGRAPH}
            color={TEXT_COLOR.GREY6}
            size={TEXT_SIZE.CAPTION1}
            text={descriptionFlyout}
          />
        )}
        
        {provisionalAgreements && (
          <div className="LegalsModal_provisionalAgreements">
            <TextInfo
              kind={TEXT_KIND.PARAGRAPH}
              color={TEXT_COLOR.GREY6}
              size={TEXT_SIZE.CAPTION1}
              text={provisionalAgreements.text}
            />
            <CustomLink
              ariaLabel={provisionalAgreements.link}
              onClick={() => {
                downloadDocuments(
                  provisionalAgreements.items,
                  'ResumenContrato.zip',
                  'ResumenContrato',
                );
              }}
              taggingInfo={{
                ...taggingInfo,
                componentContent: `${provisionalAgreements.link}-${subject || ''}`,
              }}
              forceExternal
            >
              <TextInfo
                kind={TEXT_KIND.PARAGRAPH}
                text={provisionalAgreements.link}
                color={TEXT_COLOR.MOVISTAR_BLUE}
                size={TEXT_SIZE.CAPTION1}
              />
            </CustomLink>
            <TextInfo
              kind={TEXT_KIND.PARAGRAPH}
              color={TEXT_COLOR.GREY6}
              size={TEXT_SIZE.CAPTION1}
              text={provisionalAgreements.format}
            />
          </div>
        )}
        <Form key="activePromo" id="LegalsModalForm" onSubmit={onSubmit}>
          <LegalsModalForm
            urlLegal={urlLegal}
            onClickLegal={() => { setShowLegals(!showLegals) }}
            onSubmit={onSubmit}
            subject={subject}
            legalText={legalText}
            legalErrorText={legalErrorText}
            legalLinkText={legalLinkText}
            acceptText={acceptText}
            cancelText={cancelText}
            componentProduct={componentProduct}
          />
        </Form>
        {legalLinkText && (
          <CustomLink
            ariaLabel={legalLinkText}
            onClick={() => { setShowLegals(!showLegals) }}
            taggingInfo={{
              ...taggingInfo,
              componentContent: `${legalLinkText}-${subject || ''}`,
            }}
            forceExternal
            target="_blank"
          >
            <div className="LegalsModal_dropdownText">
              <TextInfo
                kind={TEXT_KIND.PARAGRAPH}
                text={legalLinkText.charAt(0).toUpperCase() + legalLinkText.slice(1)}
                color={TEXT_COLOR.MOVISTAR_BLUE}
                size={TEXT_SIZE.BODY2}
              />
              <Glyph
                id={showLegals ? ICON_ID.CheuronUp : ICON_ID.CheuronDown}
                color={TEXT_COLOR.MOVISTAR_BLUE}
                width={12}
              />
            </div>
          </CustomLink>
        )}
        {showLegals && dropdown && (
          <TextInfo
            kind={TEXT_KIND.PARAGRAPH}
            color={TEXT_COLOR.GREY6}
            size={TEXT_SIZE.CAPTION1}
            text={dropdown}
          />
        )}
      </div>
      <style jsx>
        {`
            .LegalsModal {
                background-color: #fff;
                padding: 2rem;
                border-radius: 1rem;
                display: flex;
                flex-direction: column;
                gap: 1rem;
                &_header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                }
                &_provisionalAgreements {
                  display: flex;
                  gap: 0.2rem;
                  align-items: center;
                }
                &_dropdownText {
                  display: flex;
                  gap: 0.5rem;
                  align-items: center;
                }
            }
            @media (max-width: 768px) {
                .LegalsModal {
                  padding: 1rem;
                  width: 100vw;
                  border-radius: 1rem 1rem 0rem 0rem;
                  max-height: 80dvh;
                }
             
            }
        `}
      </style>
    </>
  );
};

export default LegalsModal;
